import React, { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import {
  buildMonthYearLabel,
  formatAxisCompactCurrency,
  formatCompactCurrency,
  formatAxisCompactNumber,
  formatCompactNumber,
  formatNumber,
} from "../utils/formatters";

function card(title, content, className = "") {
  return (
    <div className={`card chart-card ${className}`.trim()}>
      <div className="card-label">{title}</div>
      {content}
    </div>
  );
}

function getPeakAnnotations(data, categories) {
  const indexed = data
    .map((value, index) => ({ index, value: Number(value) }))
    .filter((item) => Number.isFinite(item.value));

  if (!indexed.length) {
    return [];
  }

  let maxPoint = indexed[0];
  let minPoint = indexed[0];

  for (const point of indexed) {
    if (point.value > maxPoint.value) {
      maxPoint = point;
    }
    if (point.value < minPoint.value) {
      minPoint = point;
    }
  }

  const points = [
    {
      x: categories[maxPoint.index],
      y: maxPoint.value,
      marker: {
        size: 6,
        fillColor: "#0f9d58",
        strokeColor: "#ffffff",
        strokeWidth: 2,
      },
      label: {
        text: `Peak ${formatCompactCurrency(maxPoint.value)}`,
        borderColor: "#0f9d58",
        offsetY: -10,
        style: {
          background: "#0f9d58",
          color: "#ffffff",
          fontSize: "11px",
          fontWeight: 700,
        },
      },
    },
  ];

  if (maxPoint.index !== minPoint.index) {
    points.push({
      x: categories[minPoint.index],
      y: minPoint.value,
      marker: {
        size: 6,
        fillColor: "#dc2626",
        strokeColor: "#ffffff",
        strokeWidth: 2,
      },
      label: {
        text: `Low ${formatCompactCurrency(minPoint.value)}`,
        borderColor: "#dc2626",
        offsetY: 10,
        style: {
          background: "#dc2626",
          color: "#ffffff",
          fontSize: "11px",
          fontWeight: 700,
        },
      },
    });
  }

  return points;
}

function getBaseOptions() {
  return {
    chart: {
      background: "transparent",
      toolbar: { show: false },
      animations: { enabled: false },
      redrawOnWindowResize: false,
      redrawOnParentResize: true,
      fontFamily: "Segoe UI, sans-serif",
      foreColor: "#64748b",
    },
    grid: { borderColor: "#dbe3ee", strokeDashArray: 3 },
    tooltip: { theme: "light" },
  };
}

function getRevenuePointTime(point) {
  const rawMonth = point?.month || point?.month_label || "";
  const parsed = new Date(rawMonth);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.getTime();
  }

  const labelDate = new Date(`1 ${point?.month_label || ""}`);
  return Number.isNaN(labelDate.getTime()) ? 0 : labelDate.getTime();
}

export default function ChartsSection({
  revenueTrend,
  topProducts,
  selectedCustomerId,
  terminology = {},
}) {
  const [revenueRange, setRevenueRange] = useState("lastYear");
  const customerLabel = terminology.customer_label || "Customer";
  const customerPlural = terminology.customer_plural_label || "Customers";
  const revenueLabel = terminology.revenue_label || "Revenue";
  const topItemsTitle = terminology.top_items_title || "Top Product Categories";
  const topItemsSeriesLabel = terminology.top_items_series_label || "Uses";
  const revenueRangeOptions = useMemo(
    () => [
      { key: "lastQuarter", label: "Last Quarter", months: 3 },
      { key: "lastSixMonths", label: "Last 6 Months", months: 6 },
      { key: "lastYear", label: "Last Year", months: 12 },
      { key: "custom", label: "Custom", months: null },
    ],
    []
  );
  const activeRevenueRange =
    revenueRangeOptions.find((option) => option.key === revenueRange) || revenueRangeOptions[0];

  const revenueData = useMemo(() => {
    const fallbackYear = new Date().getFullYear();
    const sortedSeries = [...(revenueTrend.series || [])].sort(
      (a, b) => getRevenuePointTime(a) - getRevenuePointTime(b)
    );
    const latestSeries = activeRevenueRange.months
      ? sortedSeries.slice(-activeRevenueRange.months)
      : sortedSeries;
    const categories = latestSeries.map((point) =>
      point.month_label || buildMonthYearLabel(point.month, fallbackYear)
    );
    const allCustomers = latestSeries.map((point) => point.cohort_revenue || 0);
    const selectedCustomer = latestSeries.map((point) => point.customer_revenue);
    const activeData = selectedCustomerId ? selectedCustomer : allCustomers;
    const annotations = getPeakAnnotations(activeData, categories);

    const series = selectedCustomerId
      ? [{ name: `Selected ${customerLabel}`, data: selectedCustomer, type: "line" }]
      : [{ name: `All ${customerPlural}`, data: allCustomers, type: "area" }];

    return {
      series,
      options: {
        ...getBaseOptions(),
        xaxis: {
          type: "category",
          categories,
          labels: {
            rotate: -45,
            formatter: (value) => value,
            style: { colors: "#64748b" },
          },
        },
        tooltip: {
          ...getBaseOptions().tooltip,
          x: {
            formatter: (_value, { dataPointIndex }) =>
              categories[dataPointIndex] || "",
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => formatAxisCompactCurrency(value),
            style: { colors: "#64748b" },
          },
        },
        annotations: {
          points: annotations,
        },
        markers: {
          size: 0,
          hover: {
            size: 5,
          },
        },
        colors: selectedCustomerId ? ["#dc2626"] : ["#1a6ef5"],
        fill: {
          type: selectedCustomerId ? ["solid"] : ["gradient"],
          gradient: { opacityFrom: 0.12, opacityTo: 0.0 },
        },
        stroke: { curve: "smooth", width: [2] },
      },
    };
  }, [
    activeRevenueRange.months,
    customerLabel,
    customerPlural,
    revenueTrend.series,
    selectedCustomerId,
  ]);

  const topProductsData = useMemo(() => {
    const sorted = [...topProducts.items].sort((a, b) => (b.count || 0) - (a.count || 0));
    const categories = sorted.map((item) => item.item_type);
    const values = sorted.map((item) => item.count || 0);
    const chartHeight = Math.max(280, sorted.length * 30);

    return {
      chartHeight,
      series: [{ name: topItemsSeriesLabel, data: values }],
      options: {
        ...getBaseOptions(),
        chart: {
          ...getBaseOptions().chart,
          height: chartHeight,
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 5,
            barHeight: "58%",
            dataLabels: {
              position: "top",
            },
          },
        },
        grid: {
          ...getBaseOptions().grid,
          show: false,
          padding: {
            left: 6,
            right: 18,
          },
        },
        xaxis: {
          categories,
          labels: {
            formatter: (value) =>
              typeof value === "string" ? value : formatAxisCompactNumber(value),
            style: { colors: "#64748b" },
          },
        },
        yaxis: {
          labels: {
            maxWidth: 180,
            formatter: (value) => {
              const text = String(value || "");
              return text.length > 24 ? `${text.slice(0, 24)}...` : text;
            },
            style: { colors: ["#4f648a"] },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (value) => formatCompactNumber(value),
          textAnchor: "start",
          offsetX: 10,
          style: { colors: ["#1f345f"], fontWeight: 800 },
          background: {
            enabled: true,
            borderRadius: 5,
            padding: 4,
            foreColor: "#1f345f",
            borderWidth: 0,
            borderColor: "transparent",
            backgroundColor: "#e6efff",
            opacity: 1,
          },
        },
        tooltip: {
          ...getBaseOptions().tooltip,
          y: {
            formatter: (value) => formatNumber(value),
          },
        },
        colors: ["#1a6ef5"],
      },
    };
  }, [topItemsSeriesLabel, topProducts.items]);

  return (
    <section className="charts-grid">
      {card(
        topItemsTitle,
        <div className="top-products-scroll">
          <Chart
            options={topProductsData.options}
            series={topProductsData.series}
            type="bar"
            height={topProductsData.chartHeight}
          />
        </div>,
        "top-products-card"
      )}

      {card(
        `${revenueLabel} Trend`,
        <div className="revenue-trend-panel">
          <div className="revenue-trend-range">
            <label htmlFor="revenue-trend-range-select">{revenueLabel} Range</label>
            <select
              id="revenue-trend-range-select"
              value={revenueRange}
              onChange={(event) => setRevenueRange(event.target.value)}
              aria-label={`${revenueLabel} trend range`}
            >
              {revenueRangeOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <Chart
            key={revenueRange}
            options={revenueData.options}
            series={revenueData.series}
            type="line"
            height={280}
          />
        </div>,
        "revenue-trend-card"
      )}
    </section>
  );
}
